import { useSuspenseQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { Suspense } from 'react';
import { useHostsQuery, usePrefetchHostsCount } from '#api/hosts.queries.ts';
import { DEFAULT_HOSTS_PAGE_SIZE, MIN_SEARCH_LENGTH } from '#api/hosts.ts';
import { maxNetworksQueryOptions, useSuspenseNetworkCount } from '#api/networks.queries.ts';
import { usePrefetchMaxTagsQuery } from '#api/tags.queries.ts';
import { FilterButton } from '#components/atoms/buttons/filter-button/FilterButton.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { SelectNetworkPersisted } from '#components/patterns/entity-select/network/SelectNetworkPersisted.tsx';
import { FilterPills } from '#components/patterns/header-buttons/filter-pill/FilterPills.tsx';
import { HeaderButtons } from '#components/patterns/header-buttons/HeaderButtons.tsx';
import { HostFilterForm } from '#components/patterns/host-filter/HostFilterForm.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { SMALLEST_PAGE_SIZE, TableFooter } from '#components/patterns/table/table-footer/TableFooter.tsx';
import { useHostFilters } from '#hooks/useHostFilters.ts';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { useUncoveredLighthouseNetworks } from '#hooks/useNetworkCoverage.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { useSavedPageSize } from '#hooks/useSavedPageSize.tsx';
import { EmptyFilteredHosts } from '#screens/hosts/empty/EmptyFilteredHosts.tsx';
import { EmptyNetworkHosts } from '#screens/hosts/empty/EmptyNetworkHosts.tsx';
import { HostsTable } from '#screens/hosts/hosts-table/HostsTable.tsx';
import { EmptyLighthouses } from '#screens/lighthouses/empty/EmptyLighthouses.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { EmptyRelays } from '#screens/relays/empty/EmptyRelays.tsx';
import { hostKind, isHostKind } from '#util/host.ts';
import { EmptyHosts } from '../empty/EmptyHosts.tsx';
import { HostSearch } from './HostSearch.tsx';
import { NetworkCoverageBanner } from './NetworkCoverageBanner.tsx';
import styles from './HostsPage.module.css';

/** Viewport width (px string) below which the multi-network header stacks. The binding band sits
 * just above `--size-breakpoint-s` (915px), where the nav sidebar (199px) and the wide body padding
 * (80px) enter the math: the unshrinkable controls — network select, filter, split add (415px) —
 * plus two 16px column gaps, the search at its 150px `min-width` floor, and the rendered "Hosts"
 * title (84px) need 199 + 80 + 415 + 32 + 150 + 84 = 960px, plus 2px of rounding allowance,
 * measured in Chromium. Below 915px the sidebar collapses and the row would fit again from ~710px
 * up, but a second unstack band between 710px and 915px isn't worth the layout flip-flopping as
 * the viewport grows — the header stays stacked until the row fits alongside the sidebar. The
 * controls are `flex-shrink: 0` (see HeaderButtons), so a mismeasured threshold shows up as
 * horizontal overflow, not wrapped button labels — re-measure, don't estimate. */
const MULTI_NETWORK_STACK_WIDTH = '963px';

/** Single-network variant of the same measurement, minus the sidebar band (its row fits well below
 * 915px) and the network select: title (84px), two 16px gaps, the 150px search floor, filter +
 * split add at their sub-915px size (213px), and 32px body padding = 511px, plus 2px allowance.
 * The shared `--size-breakpoint-xs` (500px) this replaces left an 11px overflow band above it. */
const SINGLE_NETWORK_STACK_WIDTH = '513px';

/**
 * {@link HostsPage} is the list page for all hosts — plain hosts, lighthouses, and relays in one
 * collection. It owns the URL-driven filters/search/cursor, the hosts query, the responsive page
 * header (search, network select, filter, split add button), pagination, and the empty states. The
 * user-facing `type` filter narrows the list to one kind, and drives the kind-specific extras: the
 * kind-scoped empty states, and the coverage banners (both shown on the unfiltered list, since a
 * coverage gap is a fact about the account, not about the active filter). The current page being
 * viewed is stored in the url, to enable bookmarking, sharing, and refreshing the page.
 */
export function HostsPage() {
  const [pageSize, setPageSize] = useSavedPageSize({
    defaultPageSize: DEFAULT_HOSTS_PAGE_SIZE,
  });
  const { cursor, filters, setCursor, search } = useHostFilters();

  // The `type` filter comes from the URL, so validate before branching on it — an unrecognized
  // value renders the unfiltered list (matching what the API is sent; see createEncodedGetHostsFilter).
  const typeFilter = typeof filters.type === 'string' && isHostKind(filters.type) ? filters.type : undefined;

  // HostSearch (rendered below) owns the input and writes the term to the URL; here we only read it
  // to drive the query. Re-check the minimum length: HostSearch only ever writes valid terms, but
  // the URL is untrusted input (a bookmarked/edited `?q=a` would otherwise 400 and wedge the page).
  const activeSearch = search.trim().length >= MIN_SEARCH_LENGTH ? search : '';

  const hostsQuery = useHostsQuery(cursor, pageSize, {
    ...filters,
    ...(activeSearch ? { search: activeSearch } : {}),
  });
  const networkCount = useSuspenseNetworkCount().data;
  const persistedNetworkFilter = usePersistedNetworkFilter();

  // An unmatched search is "no results for this term", never a statement about what the account or
  // network holds — so it outranks every criteria-shaped empty state in the ladder below.
  const isSearching = Boolean(activeSearch);

  // One boolean decides when the header stacks into a column, and drives everything that must flip
  // with it: PageHeader's layout (`stacked`), the buttons' collapsed variant, the search's full
  // width, the subtitle hand-off (the collapsed buttons render their own FilterPills), and the DOM
  // order below. We reorder the DOM itself (not CSS `order`) so the tab/reading order always matches
  // the visual order (WCAG 2.4.3 / 1.3.2) — in the row the search sits left of the controls;
  // stacked, below them, directly above the list it filters.
  //
  // The thresholds carry the measured minimum row widths — see the constants' derivations above
  // (originally <https://github.com/DefinedNet/webclient/issues/3889>). The search's 280px→150px
  // shrink range is the row's only give: the controls pin `flex-shrink: 0` (HeaderButtons) so
  // button labels never wrap, and once the search bottoms out at its floor the row overflows. The
  // header must therefore stack before that point, and any control that gains width must be
  // re-measured into the constants rather than absorbed.
  const stackBelow = networkCount > 1 ? MULTI_NETWORK_STACK_WIDTH : SINGLE_NETWORK_STACK_WIDTH;
  const isStacked = useMatchMedia(`(max-width: ${stackBelow})`);

  const { data: hosts, metadata } = hostsQuery.data ?? {};
  const hasHosts = Boolean(hosts?.length);

  {
    // prefetch for the add page
    usePrefetchHostsCount();
    // prefetch for InlineTagsList
    usePrefetchMaxTagsQuery();
  }

  if (!hosts || !metadata) return <LoadingSpinner />;
  // The hosts query keeps the previous page's data while a new key loads, so an empty result can
  // outlive the criteria that produced it: clear a zero-result search and `activeSearch` is already
  // `''` while `hosts` is still the old empty page. Every empty state below reads that combination as
  // a fact about the account and would announce it has nothing. Zero rows means there is no previous
  // content worth keeping anyway, so treat that placeholder as still loading — but only in the table
  // area. A full-page spinner would unmount the header, and with it the search input: editing a term
  // from "No results found" refetches through this exact state on every keystroke, and remounting
  // the input drops its focus each time.
  const isEmptyPlaceholder = hostsQuery.isPlaceholderData && !hasHosts;
  // Same placeholder mechanism, different lie: after a type-filter change the previous page's rows
  // are the wrong *kind* — plain hosts dimmed under a fresh "Type: Lighthouse" pill read as the
  // filter being stuck, and on a slow account that window is long. A cursor change may keep its
  // placeholder (same view, one page back); rows that contradict the type filter render as loading
  // instead.
  if (hostsQuery.isPlaceholderData && typeFilter && hosts.some((host) => hostKind(host) !== typeFilter)) {
    return <LoadingSpinner />;
  }

  // The type filter narrows which kind the list shows; the ladder below asks "did the user apply
  // any *criteria* beyond that view choice", so it counts the other filters only. This keeps the
  // redirected legacy URLs (`/hosts?filter.type=lighthouse`) behaving exactly like the standalone
  // pages they replace.
  const appliedFilters = Object.keys(filters).filter((name) => name !== 'type');
  const isNetworkFiltered = appliedFilters.includes('networkID');
  const onlyNetworkFilter = appliedFilters.length === 1 && isNetworkFiltered;

  // Where "clear filters" and kind-specific add CTAs land: keep the type filter (it's the view, not
  // a criterion) and, for clearing, the ambient network scope.
  const listSearchParams = new URLSearchParams(isNetworkFiltered ? persistedNetworkFilter : undefined);
  if (typeFilter) listSearchParams.set('filter.type', typeFilter);
  const listSearch = listSearchParams.toString();
  const clearFiltersHref = {
    pathname: '/hosts',
    search: listSearch ? `?${listSearch}` : undefined,
  };
  const addHref = typeFilter && typeFilter !== 'host' ? `add?tab=general&type=${typeFilter}` : 'add?tab=general';

  // Where "Show all networks" lands: the inverse of clearing criteria — drop the network scope,
  // keep the type view. `clearFiltersHref` treats the network as ambient scope and keeps it, which
  // here would link back to the current URL.
  const allNetworksHref = {
    pathname: '/hosts',
    search: typeFilter ? `?filter.type=${typeFilter}` : undefined,
  };

  // Full-page empty state, reserved for the unfiltered view of an account with no hosts at all -
  // there is nothing to search or filter, so replacing the header costs nothing. With a type
  // filter active the full page would take the header and Type pill with it, leaving no on-page
  // way to remove the filter just applied - so every kind view keeps the header and renders its
  // empty state inside the table area (the ladder at the bottom of this component).
  if (!hasHosts && !isEmptyPlaceholder && !appliedFilters.length && !activeSearch && typeFilter === undefined) {
    return <EmptyHosts addHostHref={addHref} />;
  }

  // "No results found" — the fallback for any criteria that matched nothing. Clearing keeps the
  // network (the filter users treat as ambient scope) and the type view, and drops everything else,
  // including `q`. Except: when the type view is the *only* thing narrowing the list, keeping it
  // would link right back to this empty page, so the CTA must drop `filter.type` itself.
  const emptyFiltered = (
    <EmptyFilteredHosts
      hostNoun={typeFilter ?? 'host'}
      clearFiltersHref={appliedFilters.length || activeSearch ? clearFiltersHref : { pathname: '/hosts' }}
    />
  );

  // Stable keys: the stacked/row fragments below swap these two elements' positions. Keyed, the
  // swap is a move; unkeyed, React remounts both by position — wiping the search's uncommitted
  // input and focus when e.g. browser zoom crosses the stack threshold mid-typing.
  const searchInput = <HostSearch key="search" label="Search hosts" fullWidth={isStacked} />;
  const headerButtons = (
    <HeaderButtons
      key="buttons"
      addTo="add?tab=general"
      addLabel="Add host"
      addMenuItems={[
        { label: 'Add lighthouse', to: 'add?tab=general&type=lighthouse' },
        { label: 'Add relay', to: 'add?tab=general&type=relay' },
      ]}
      alwaysGroupAdditional={true}
      collapsed={isStacked}
      additionalButtons={
        networkCount > 1 ?
          [
            {
              key: '1',
              // The select is the row's wide control: stacked, it keeps a readable floor (via
              // `primary`) and fills the rest, rather than collapsing to an even third beside the
              // filter/add icon buttons.
              primary: true,
              element: (
                <SelectNetworkPersisted
                  className={cn(styles.HostsPage_networkSelect, {
                    [styles.HostsPage_networkSelect___stacked]: isStacked,
                  })}
                  size="small"
                  menuClassName={styles.HostsPage_networkSelectMenu}
                />
              ),
            },
            {
              key: '2',
              element: (
                <Tooltip
                  content="Filter"
                  placement="bottom"
                  flip="bottom"
                  render={<FilterButton>{(onApply) => <HostFilterForm onApply={onApply} />}</FilterButton>}
                />
              ),
            },
          ]
        : [
            {
              key: '1',
              element: <FilterButton>{(onApply) => <HostFilterForm onApply={onApply} />}</FilterButton>,
            },
          ]
      }
    />
  );

  // A coverage banner owns its own data fetch and renders nothing when no network has a gap. On the
  // unfiltered list both kinds' banners mount — a coverage gap is a fact about the account either
  // way — while the kind views mount only their own, and the plain-host view none, which keeps that
  // view free of the per-network count fan-out. Banners always sit in the subtitle, so "banner
  // rendered" is a DOM fact the subtitle CSS keys off (`:has(.HostsPage_banner)`).
  //
  // The banners suspend on that fan-out (and suspending siblings resolve serially), so they need
  // their own boundary: without one the nearest fallback is the route-level spinner, and first
  // paint of the whole list would block on up to two networks' worth of count requests. With
  // `fallback={null}` the list paints immediately and the banners pop in after — the subtitle CSS
  // keys off the eventual DOM either way.
  const bannerEl = (
    <Suspense fallback={null}>
      {typeFilter === undefined || typeFilter === 'lighthouse' ?
        <NetworkCoverageBanner kind="lighthouse" className={styles.HostsPage_banner} />
      : null}
      {typeFilter === undefined || typeFilter === 'relay' ?
        <NetworkCoverageBanner kind="relay" className={styles.HostsPage_banner} />
      : null}
    </Suspense>
  );

  return (
    <TableLayout
      isEmpty={!hasHosts}
      header={
        <PageHeader
          title="Hosts"
          stacked={isStacked}
          // Stacked, the collapsed HeaderButtons render their own FilterPills below the controls, so
          // the subtitle carries only the banner. Non-stacked, the pills live here — a dedicated row
          // beneath the header controls, where they can wrap freely instead of fighting the search
          // and buttons for the row's leftover width. The subtitle CSS collapses the wrapper
          // whenever it ends up empty.
          subtitle={
            isStacked ? bannerEl : (
              <>
                {/* The subtitle's own 8px top margin collapses into this mt-24, netting 24px of
                    air below the header controls — the same spacing the collapsed HeaderButtons
                    give their pills. */}
                <FilterPills className="mt-24" />
                {bannerEl}
              </>
            )
          }
          subtitleClassName={styles.HostsPage_subtitle}
          preventTitleWrap={true}
        >
          {isStacked ?
            <>
              {headerButtons}
              {searchInput}
            </>
          : <>
              {searchInput}
              {headerButtons}
            </>
          }
        </PageHeader>
      }
      pagination={
        hasHosts && metadata.totalCount > SMALLEST_PAGE_SIZE ?
          <TableFooter
            entityType={typeFilter ? `${typeFilter}s` : 'hosts'}
            pageSize={pageSize}
            metadata={metadata}
            isFetching={hostsQuery.isFetching}
            setCursor={setCursor}
            onPageSizeChange={setPageSize}
          />
        : null
      }
    >
      {isEmptyPlaceholder ?
        <LoadingSpinner />
      : hasHosts ?
        <HostsTable data={hosts} isFetching={hostsQuery.isFetching} dataUpdatedAt={hostsQuery.dataUpdatedAt} />
      : isSearching ?
        emptyFiltered
      : typeFilter === 'lighthouse' && !appliedFilters.length ?
        // No lighthouse hosts and no criteria beyond the view: the onboarding state, in the table
        // area so the header and Type pill survive. Managed lighthouses are not hosts, so they
        // never fill this list — which copy is honest depends on whether they already cover every
        // network, and that read suspends (see EmptyLighthousesWithCoverage below).
        <Suspense fallback={null}>
          <EmptyLighthousesWithCoverage addLighthouseHref={addHref} className={styles.HostsPage_kindEmptyState} />
        </Suspense>
      : typeFilter === 'relay' && !appliedFilters.length ?
        // No relays on the account and no criteria beyond the view: the "dedicated relays
        // recommended" onboarding, likewise inline.
        <EmptyRelays addRelayHref={addHref} className={styles.HostsPage_kindEmptyState} />
      : onlyNetworkFilter ?
        <EmptyNetworkHosts
          hostNoun={typeFilter ?? 'host'}
          addHostHref={`/hosts/${addHref}`}
          clearFiltersHref={allNetworksHref}
        />
      : emptyFiltered}
    </TableLayout>
  );
}

/**
 * Reads lighthouse coverage for {@link EmptyLighthouses}. Split out because the read suspends on a
 * per-network count fan-out; keeping it in its own component lets the page hold it behind a
 * `fallback={null}` boundary rather than blocking the table area on it. NetworkCoverageBanner runs
 * the same hook, so on the lighthouse view React Query serves both from one set of requests.
 */
function EmptyLighthousesWithCoverage({
  addLighthouseHref,
  className,
}: {
  addLighthouseHref: string;
  className?: string;
}) {
  const networks = useSuspenseQuery(maxNetworksQueryOptions).data.data;
  // Reached only with zero lighthouse hosts, so every network that isn't reported uncovered is
  // covered by a managed lighthouse.
  const isManagedCovered = useUncoveredLighthouseNetworks(networks).length === 0;
  return (
    <EmptyLighthouses addLighthouseHref={addLighthouseHref} isManagedCovered={isManagedCovered} className={className} />
  );
}
